import { PatientActionTypes, PatientActions } from './patient.actions';
import { ServiceError } from '@cigna/chcp/shared/util';

import {
  PatientEstimateCostResult,
  PatientSearchRow,
  PatientResultObject,
  PrimaryCareDetails,
  PlanLevelAccumulation,
  BenefitReportDetails,
  Contact,
  Alliance,
  CcfBenefit,
  CoverageDetail,
  CostEstimationDetail,
  PatientDetailsObj,
  UserDetail,
  ServiceLevelAccumulation,
  CobDetails,
  MaternityBenefitDetails,
  HealthWellness,
  PharmacyBenefitDetails,
  PharmacyResponse,
  NotificationDetail,
  ProductDetail,
  IdCardDetails,
  DentalOfficeDetails,
  FrequencyAgePlanDetails,
  VisionServices,
  InfertilityServices,
  SureFitResponse,
  Tin,
  PrecertData,
  BRNPayload,
  PharmacyPlanResponse,
  AmInNetworkStatus,
  PAGE_STAGE,
} from '@cigna/chcp/patient/shared/util';

export const PATIENT_FEATURE_KEY = 'patient';

export interface PatientState {
  pageStage: PAGE_STAGE;
  amInNetworkStatus: AmInNetworkStatus;
  eligibilityDate: string;
  searches: PatientSearchRow[];
  results: PatientResultObject[];
  resultFilter: string;
  multipleMatched: boolean;
  noneMatched: boolean;
  singleResponse: boolean;
  patientRowDetailLoading: any;
  patientEstimateDetailsLoading: any;
  patientEstimateResultLoading: any;
  patientRowInactiveLoading: any;
  patientDetail: PatientDetailsObj | null;
  benefitReportDetails: BenefitReportDetails | null;
  displayPatientDetails: boolean;
  alliances: Alliance | null;
  ccfBenefit: CcfBenefit | null;
  coverageDetail: CoverageDetail | null;
  estimateType: string;
  costEstimationDetail: CostEstimationDetail | null;
  serviceProcedures: any;
  estimateCostResult: PatientEstimateCostResult[];
  subscriberDetail: UserDetail | null;
  serviceContacts: Contact[];
  planLevelAccumulation: PlanLevelAccumulation[];
  primaryCareDetails: PrimaryCareDetails | null;
  serviceLevelAccumulation: ServiceLevelAccumulation | null;
  maternityBenefitDetails: MaternityBenefitDetails | null;
  healthWellness: HealthWellness | null;
  patientCompositeKey: string;
  cobDetails: CobDetails[] | null;
  notificationDetail: NotificationDetail | null;
  pharmacyBenefitDetails: PharmacyBenefitDetails | null;
  providerGroup: any;
  providers: any;
  providerDetails: any;
  inOutNetworkDetails: any;
  idCardDetails: IdCardDetails | null;
  idCardError: ServiceError | null;
  error: ServiceError | null;
  submitEstimateError: any;
  productDetails: ProductDetail | null;
  dentalOfficeDetails: DentalOfficeDetails | null;
  frequencyAgePlanDetails: FrequencyAgePlanDetails | null;
  visionServices: VisionServices | null;
  infertilityServices: InfertilityServices | null;
  sureFitResponse: SureFitResponse | null;
  ohpTin: Tin | null;
  precertData: PrecertData | null;
  reference: BRNPayload | null;
  networkCode: string;
  medAmInNetworkSelectedTin: string;
  enhanced: boolean;
}

export interface PatientPartialState {
  readonly [PATIENT_FEATURE_KEY]: PatientState;
}

export const initialState: PatientState = {
  pageStage: PAGE_STAGE.default,
  amInNetworkStatus: { status: false, accumListCheckCSN: false },
  eligibilityDate: '',
  searches: [],
  results: [],
  resultFilter: 'all',
  multipleMatched: false,
  noneMatched: false,
  singleResponse: false,
  patientRowDetailLoading: null,
  patientEstimateDetailsLoading: null,
  patientEstimateResultLoading: null,
  patientRowInactiveLoading: null,
  patientDetail: null,
  benefitReportDetails: null,
  displayPatientDetails: true,
  alliances: null,
  ccfBenefit: null,
  coverageDetail: null,
  estimateType: 'OP',
  costEstimationDetail: null,
  serviceProcedures: null,
  estimateCostResult: [],
  subscriberDetail: null,
  serviceContacts: [],
  planLevelAccumulation: [],
  primaryCareDetails: null,
  serviceLevelAccumulation: null,
  maternityBenefitDetails: null,
  healthWellness: null,
  patientCompositeKey: '',
  cobDetails: null,
  pharmacyBenefitDetails: null,
  providerGroup: null,
  providers: null,
  providerDetails: null,
  inOutNetworkDetails: null,
  idCardDetails: null,
  idCardError: null,
  error: null,
  submitEstimateError: null,
  notificationDetail: null,
  productDetails: null,
  dentalOfficeDetails: null,
  frequencyAgePlanDetails: null,
  visionServices: null,
  infertilityServices: null,
  sureFitResponse: null,
  ohpTin: null,
  precertData: null,
  reference: null,
  networkCode: '',
  medAmInNetworkSelectedTin: '',
  enhanced: false,
};

const getPharmacyFacetDetails = (
  pharmacyResponse: PharmacyResponse | null,
): PharmacyBenefitDetails | null => {
  if (pharmacyResponse?.pharmacyBenefitDetails) {
    return pharmacyResponse.pharmacyBenefitDetails;
  }
  return null;
};

const getNetworkcode = (
  pharmacyPlanResponse: PharmacyPlanResponse | null,
): string => {
  if (
    pharmacyPlanResponse &&
    pharmacyPlanResponse.pharmacyPlanDetail &&
    pharmacyPlanResponse.pharmacyPlanDetail.networkInfo &&
    pharmacyPlanResponse.pharmacyPlanDetail.networkInfo.code
  ) {
    return pharmacyPlanResponse.pharmacyPlanDetail.networkInfo.code;
  }
  return '';
};

const isActiveMember = (data: any): boolean => {
  if (
    data?.patientDetail?.asOfDate &&
    data?.coverageDetail?.effectiveFrom &&
    data?.coverageDetail?.effectiveTill
  ) {
    if (
      new Date(data.coverageDetail.effectiveFrom).getTime() <=
        new Date(data.patientDetail.asOfDate).getTime() &&
      new Date(data.patientDetail.asOfDate).getTime() <=
        new Date(data.coverageDetail.effectiveTill).getTime()
    ) {
      return true;
    }
    return false;
  }
  return false;
};

const isTuftsMemeber = (data: any): boolean => {
  let isTuftMember = false;
  if (
    data?.alliances?.partners?.length &&
    data.alliances.partners[0].roleOfCigna === 'SP' &&
    data.alliances.partners[0].coordinatingParty === 'THP01'
  ) {
    isTuftMember = true;
  }
  return isTuftMember;
};

const isMVPWithRoleCode = (alliances: Alliance): boolean =>
  alliances?.hasMVP && alliances.partners?.[0]?.roleOfCigna === 'SP';

const showPatientDetails = (data: any): boolean => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  let showDetails = false;
  if (data?.coverageDetail?.claimSystemCode === 'QCRE') {
    return false;
  }
  if (
    data?.coverageDetail?.coverageStatus &&
    ['A', 'ACC'].includes(data.coverageDetail.coverageStatus)
  ) {
    showDetails = true;
  }
  if (isTuftsMemeber(data) && isActiveMember(data)) {
    showDetails = false;
  }
  if (isMVPWithRoleCode(data?.alliances) && isActiveMember(data)) {
    showDetails = false;
  }
  if (data?.coverageDetail?.hasConflictingStatus) {
    showDetails = false;
  }
  return showDetails;
};

// TODO: resolve CHCP disables by bot
// eslint-disable-next-line sonarjs/cognitive-complexity
export function patientReducer(
  state = initialState,
  action: PatientActions,
): PatientState {
  // TODO: resolve CHCP disables by bot
  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (action.type) {
    case PatientActionTypes.NewSearch:
    case PatientActionTypes.ResetSearch: {
      return initialState;
    }
    case PatientActionTypes.ModifySearch: {
      return {
        ...state,
        amInNetworkStatus: { status: false, accumListCheckCSN: false },
        results: [],
        resultFilter: 'all',
        multipleMatched: false,
        noneMatched: false,
        singleResponse: false,
        patientRowDetailLoading: null,
        patientEstimateDetailsLoading: null,
        patientEstimateResultLoading: null,
        patientRowInactiveLoading: null,
        patientDetail: null,
        alliances: null,
        ccfBenefit: null,
        coverageDetail: null,
        subscriberDetail: null,
        primaryCareDetails: null,
        serviceLevelAccumulation: null,
        maternityBenefitDetails: null,
        healthWellness: null,
        cobDetails: null,
        notificationDetail: null,
        patientCompositeKey: '',
        providerGroup: null,
        providers: null,
        providerDetails: null,
        inOutNetworkDetails: null,
        idCardDetails: null,
        error: null,
        dentalOfficeDetails: null,
        frequencyAgePlanDetails: null,
        productDetails: null,
        pharmacyBenefitDetails: null,
        visionServices: null,
        infertilityServices: null,
      };
    }
    case PatientActionTypes.SearchPatients: {
      return {
        ...state,
        pageStage: PAGE_STAGE.submitting,
        eligibilityDate: action.payload.asOfDate,
        searches: action.payload.patients,
        ohpTin: null,
      };
    }
    case PatientActionTypes.SearchPatientsSuccess: {
      return {
        ...state,
        pageStage: PAGE_STAGE.default,
        results: action.payload.patients,
        patientDetail: null,
        coverageDetail: null,
        multipleMatched: action.payload.searchInfo.multipleResponse,
        noneMatched: action.payload.searchInfo.noneMatched,
        singleResponse: action.payload.searchInfo.singleResponse,
      };
    }
    case PatientActionTypes.SearchPatientsError: {
      return {
        ...state,
        pageStage: PAGE_STAGE.error,
        error: action.error.errObject,
      };
    }
    case PatientActionTypes.InactiveMemberSearch: {
      return {
        ...state,
        patientRowInactiveLoading: action.payload.uniqueIdentifier,
      };
    }
    case PatientActionTypes.InactiveMemberSearchSuccess: {
      return {
        ...state,
        patientRowInactiveLoading: null,
        resultFilter: 'all',
        results: state.results.map((result) =>
          result.uniqueIdentifier === action.payload.uniqueIdentifier
            ? action.payload
            : result,
        ),
      };
    }
    case PatientActionTypes.InactiveMemberSearchError: {
      return {
        ...state,
        patientRowInactiveLoading: null,
      };
    }
    case PatientActionTypes.ChangeResultFilter: {
      return {
        ...state,
        resultFilter: action.payload,
      };
    }
    case PatientActionTypes.GetPharmacyFacets: {
      return {
        ...state,
        pharmacyBenefitDetails: null,
      };
    }
    case PatientActionTypes.GetPatientDetail: {
      return {
        ...state,
        pageStage: PAGE_STAGE.submitting,
        patientCompositeKey: action.data.key,
        alliances: null,
        ccfBenefit: null,
        idCardDetails: null,
        subscriberDetail: null,
        patientRowDetailLoading: true,
        serviceContacts: [],
        planLevelAccumulation: [],
        primaryCareDetails: null,
        serviceLevelAccumulation: null,
        maternityBenefitDetails: null,
        healthWellness: null,
        cobDetails: null,
        pharmacyBenefitDetails: null,
        notificationDetail: null,
        productDetails: null,
        dentalOfficeDetails: null,
        frequencyAgePlanDetails: null,
        visionServices: null,
        infertilityServices: null,
        benefitReportDetails: null,
        reference: null,
        sureFitResponse: null,
      };
    }
    case PatientActionTypes.GetPatientDetailSuccess: {
      return {
        ...state,
        pageStage: PAGE_STAGE.success,
        patientDetail: action.data.patientDetail,
        alliances: action.data.alliances,
        displayPatientDetails: showPatientDetails(action.data),
        ccfBenefit: action.data.ccfBenefit,
        coverageDetail: action.data.coverageDetail,
        idCardDetails: action.data.idCardDetails,
        subscriberDetail: action.data.subscriberDetail,
        patientRowDetailLoading: true,
        patientCompositeKey: action.compositeKey || state.patientCompositeKey,
        serviceContacts: action.data.serviceContacts,
        planLevelAccumulation: action.data.planLevelAccumulation,
        benefitReportDetails: action.data.benefitReportDetails,
        primaryCareDetails: action.data.primaryCareDetails,
        serviceLevelAccumulation: action.data.serviceLevelAccumulation,
        maternityBenefitDetails: action.data.maternityBenefitDetails,
        healthWellness: action.data.healthWellness,
        eligibilityDate: action.data.patientDetail.asOfDate,
        cobDetails: action && action.data ? action.data.cobDetails : null,
        pharmacyBenefitDetails:
          action && action.data ? action.data.pharmacyBenefitDetails : null,
        notificationDetail:
          action && action.data ? action.data.notificationDetail : null,
        productDetails: action.data.productDetails,
        dentalOfficeDetails: action.data.dentalOfficeDetails,
        frequencyAgePlanDetails: action.data.frequencyAgePlanDetails,
        visionServices: action.data.visionServices,
        infertilityServices: action.data.infertilityServices,
        reference: action && action.data ? action.data.reference : null,
        error: action.data.runtimeError,
      };
    }
    case PatientActionTypes.HydratePatientDataFromCoverages: {
      return {
        ...state,
        coverageDetail: action.payload.coverage,
        patientDetail: action.payload.patient,
        alliances: action.payload.alliance,
        patientCompositeKey: action.payload.compositeKey,
        reference: action.payload.reference || null,
        enhanced: true,
      };
    }
    case PatientActionTypes.HydrateIdCardFromCoverages: {
      return {
        ...state,
        idCardDetails: action?.payload.idCardDetails || null,
        idCardError: action?.payload.error || null,
      };
    }
    case PatientActionTypes.ResetDetails: {
      return {
        ...state,
        ccfBenefit: null,
        idCardDetails: null,
        subscriberDetail: null,
        patientRowDetailLoading: true,
        serviceContacts: [],
        planLevelAccumulation: [],
        primaryCareDetails: null,
        serviceLevelAccumulation: null,
        maternityBenefitDetails: null,
        healthWellness: null,
        cobDetails: null,
        pharmacyBenefitDetails: null,
        notificationDetail: null,
        productDetails: null,
        dentalOfficeDetails: null,
        frequencyAgePlanDetails: null,
        visionServices: null,
        infertilityServices: null,
        benefitReportDetails: null,
        reference: null,
        sureFitResponse: null,
        enhanced: false,
      };
    }
    case PatientActionTypes.GetPatientDetailError: {
      return {
        ...state,
        pageStage: PAGE_STAGE.error,
        patientRowDetailLoading: null,
        error: action.error,
      };
    }
    case PatientActionTypes.GetPharmacyFacetsSuccess: {
      return {
        ...state,
        pharmacyBenefitDetails: getPharmacyFacetDetails(
          action.pharmacyResponse,
        ),
      };
    }
    case PatientActionTypes.GetAmInNetworkStatus: {
      return {
        ...state,
        amInNetworkStatus: {
          status: action.payload.status,
          accumListCheckCSN: action.payload.accumListCheckCSN,
        },
      };
    }
    case PatientActionTypes.GetProviderGroup: {
      return {
        ...state,
        providerGroup: { data: null, type: 'loading' },
        medAmInNetworkSelectedTin: action.payload,
      };
    }
    case PatientActionTypes.GetProviderGroupSuccess: {
      return {
        ...state,
        providerGroup: {
          data: action.payload
            ? action.payload
                .slice()
                .sort((a: any, b: any) => a.name.localeCompare(b.name))
            : [],
          type: 'success',
        },
      };
    }
    case PatientActionTypes.GetProviderGroupError: {
      return {
        ...state,
        providerGroup: { data: action.error, type: 'error' },
      };
    }
    case PatientActionTypes.GetProviders: {
      return {
        ...state,
        providers: { data: null, type: 'loading' },
      };
    }
    case PatientActionTypes.GetProvidersSuccess: {
      return {
        ...state,
        providers: { data: action.payload, type: 'success' },
      };
    }
    case PatientActionTypes.GetProvidersError: {
      return {
        ...state,
        providers: { data: action.error, type: 'error' },
      };
    }

    case PatientActionTypes.GetProviderDetails: {
      return {
        ...state,
        providerDetails: { data: null, type: 'loading' },
      };
    }
    case PatientActionTypes.GetProviderDetailsSuccess: {
      return {
        ...state,
        providerDetails: { data: action.payload, type: 'success' },
      };
    }
    case PatientActionTypes.GetProviderDetailsError: {
      return {
        ...state,
        providerDetails: { data: action.error, type: 'error' },
      };
    }
    case PatientActionTypes.GetPathwellInOutNetworkDetails:
    case PatientActionTypes.GetInOutNetworkDetails: {
      return {
        ...state,
        inOutNetworkDetails: { data: null, type: 'loading' },
      };
    }
    case PatientActionTypes.GetInOutNetworkDetailsSuccess: {
      return {
        ...state,
        inOutNetworkDetails: { data: action.payload, type: 'success' },
      };
    }
    case PatientActionTypes.UpdateCompositeKey: {
      return {
        ...state,
        patientCompositeKey: action.key,
      };
    }
    case PatientActionTypes.GetInOutNetworkDetailsError: {
      return {
        ...state,
        inOutNetworkDetails: { data: action.error, type: 'error' },
      };
    }
    case PatientActionTypes.ResetErrors: {
      return {
        ...state,
        pageStage: PAGE_STAGE.default,
        error: null,
      };
    }
    case PatientActionTypes.ResetProviderData: {
      if (action.key === 'providerGroup') {
        return {
          ...state,
          providers: null,
          inOutNetworkDetails: null,
        };
      }
      return {
        ...state,
        providerGroup: null,
        providers: null,
        inOutNetworkDetails: null,
      };
    }
    case PatientActionTypes.SetEstimateType: {
      return {
        ...state,
        estimateType: action.estimateType,
      };
    }
    case PatientActionTypes.GetCostEstimatorDetail: {
      return {
        ...state,
        costEstimationDetail: null,
        patientEstimateDetailsLoading: true,
      };
    }
    case PatientActionTypes.GetCostEstimatorDetailSuccess: {
      return {
        ...state,
        costEstimationDetail: action.data.costdata,
        estimateCostResult: [],
        patientEstimateDetailsLoading: false,
      };
    }

    case PatientActionTypes.GetCostEstimatorDetailError: {
      return {
        ...state,
        error: action.error,
        estimateCostResult: [],
        patientEstimateDetailsLoading: false,
      };
    }
    case PatientActionTypes.GetServiceProcedures: {
      return {
        ...state,
        serviceProcedures: { data: null, type: 'loading' },
      };
    }
    case PatientActionTypes.GetServiceProceduresSuccess: {
      return {
        ...state,
        serviceProcedures: { data: action.data.procedure, type: 'success' },
      };
    }
    case PatientActionTypes.GetServiceProceduresError: {
      return {
        ...state,
        serviceProcedures: { data: action.error, type: 'error' },
      };
    }
    case PatientActionTypes.ResetServiceProcedures: {
      return {
        ...state,
        serviceProcedures: null,
      };
    }
    case PatientActionTypes.PatientEstimateCost: {
      return {
        ...state,
        estimateCostResult: [],
        patientEstimateResultLoading: true,
      };
    }
    case PatientActionTypes.PatientEstimateCostSuccess: {
      return {
        ...state,
        estimateCostResult: action.data.estimateDetails,
        patientEstimateResultLoading: false,
      };
    }
    case PatientActionTypes.PatientEstimateCostError: {
      return {
        ...state,
        submitEstimateError: action.error,
        patientEstimateResultLoading: false,
      };
    }
    case PatientActionTypes.ResetPatientEstimateCost: {
      return {
        ...state,
        estimateCostResult: [],
      };
    }

    case PatientActionTypes.ResetSubmitEstimateError: {
      return {
        ...state,
        submitEstimateError: null,
      };
    }
    case PatientActionTypes.SureFitSuccess: {
      return {
        ...state,
        sureFitResponse: action.payload.pharmacyPlanResponse,
        networkCode: getNetworkcode(action.payload.pharmacyPlanResponse),
        error: null,
      };
    }

    case PatientActionTypes.SureFitError: {
      return {
        ...state,
        error: action.error,
      };
    }

    case PatientActionTypes.SetOhpTin: {
      return {
        ...state,
        ohpTin: action.ohpTin,
      };
    }

    case PatientActionTypes.GetPrecertUrl: {
      return {
        ...state,
        precertData: null,
        patientRowDetailLoading: true,
      };
    }

    case PatientActionTypes.GetPrecertUrlSuccess: {
      return {
        ...state,
        precertData: {
          url: action.data.url,
          sessionKey: action.data.sessionKey,
        },
        patientRowDetailLoading: false,
      };
    }

    case PatientActionTypes.GetPrecertUrlError: {
      return {
        ...state,
        precertData: null,
        error: action.error,
        patientRowDetailLoading: false,
      };
    }

    default: {
      return state;
    }
  }
}
